import { useEffect, useState } from "react";
import {
  Card,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
  IconButton,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditUserDetails from "./edit-user-details/edit-user-details";
import DeactivateUserDialog from "./deactivate-user/deactivate-user";
import ReActivateUserDialog from "./re-activate-user/re-activate-user";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { sendPasswordResetEmailRequest } from "../../../../../utils/APICalls";
import Loading from "../../../../../components/loading/loading";
import Notification from "../../../../../components/notification/notification";
import ErrorNotification from "../../../../../components/error-notification/error-notification";

type props = {
  data: any;
  isMyOrganisation?: boolean;
};

export default function UserDetailsTab({ data, isMyOrganisation }: props) {
  const [isActive, setIsActive] = useState(false);
  const [showEditUserDetails, setShowEditUserDetails] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState<any>();
  const [altEmails, setAltEmails] = useState<any>([]);
  const [primaryPhone, setPrimaryPhone] = useState<any>();
  const [altPhones, setAltPhones] = useState<any>([]);
  const [showDeactivateUser, setShowDeactivateUser] = useState(false);
  const [showReActivateUser, setShowReActivateUser] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (data) {
      setIsActive(data?.isActive);
      const filterPrimaryEmails = data?.emails.filter(
        (email: any) => email.isPrimary === true
      );
      const filterAltEmails = data?.emails.filter(
        (email: any) => email.isPrimary === false
      );
      setPrimaryEmail(filterPrimaryEmails[0]);
      setAltEmails(filterAltEmails);
      const filterPrimaryPhones = data?.phones.filter(
        (phone: any) => phone.isPrimary === true
      );
      const filterAltPhones = data?.phones.filter(
        (phone: any) => phone.isPrimary === false
      );
      setPrimaryPhone(filterPrimaryPhones[0]);
      setAltPhones(filterAltPhones);
    }
  }, [data]);

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked === false) {
      setShowDeactivateUser(true);
    } else {
      setShowReActivateUser(true);
    }
  };

  const handleEditUserDetails = () => {
    setShowEditUserDetails(true);
  };

  const handleSendPasswordResetEmail = async () => {
    setSendingPasswordReset(true);
    try {
      const response = await sendPasswordResetEmailRequest(data?.id);

      if (response?.status === 200 || response?.status === 204) {
        setNotificationMessage("Password reset email sent successfully.");
        setShowNotification(true);
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to send password reset email. Please try again."
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("Failed to send password reset email. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setSendingPasswordReset(false);
    }
  };

  return (
    <>
      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">User Details</Typography>
          {!isMyOrganisation && (
            <Button
              variant="text"
              startIcon={<EditOutlinedIcon />}
              onClick={handleEditUserDetails}
            >
              Edit
            </Button>
          )}
        </Box>
        <Box className="display-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography className="label-txt">Status</Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {data?.isOrganizationActive === false && (
              <Tooltip
                title={
                  <Typography
                    sx={{ width: 184, textAlign: "center", fontSize: 10 }}
                  >
                    This user is associated with an inactive client. Re-activate
                    the client in order to re-activate this user
                  </Typography>
                }
                arrow
                placement="top"
              >
                <IconButton sx={{ p: 0, mr: 2 }}>
                  <LockOutlinedIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isMyOrganisation ? (
              <Typography>{isActive ? "Active" : "Inactive"}</Typography>
            ) : (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={handleStatusChange}
                      disabled={data?.isOrganizationActive === false}
                    />
                  }
                  label={isActive ? "Active" : "Inactive"}
                />
              </FormGroup>
            )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Title</Typography>
          <Typography>{data?.title ? data?.title : "-"}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Name</Typography>
          <Typography>
            {data?.firstName} {data?.lastName}
          </Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Username</Typography>
          <Typography>{data?.username}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Email</Typography>
          <Box>
            {primaryEmail && (
              <Box display={"flex"} alignItems="center">
                <Typography>{primaryEmail?.emailAddress}</Typography>
                <Chip
                  sx={{ ml: 1 }}
                  label="primary"
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              </Box>
            )}
            {altEmails?.map((email: any, index: number) => (
              <Typography key={index}>{email?.emailAddress}</Typography>
            ))}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Phone</Typography>
          <Box>
            {primaryPhone && (
              <Box display={"flex"} alignItems="center">
                <Typography>{primaryPhone?.phoneNumber}</Typography>
                <Chip
                  sx={{ ml: 1 }}
                  label="primary"
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              </Box>
            )}
            {altPhones?.map((phone: any, index: number) => (
              <Typography key={index}>{phone?.phoneNumber}</Typography>
            ))}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Reset Password</Typography>
          <Button
            sx={{ mt: 0.5, fontWeight: "510" }}
            variant="outlined"
            onClick={handleSendPasswordResetEmail}
          >
            Send Password Reset Email
          </Button>
        </Box>
      </Card>

      <Notification
        show={showNotification}
        message={notificationMessage}
        onClose={() => setShowNotification(false)}
      />

      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />

      {showEditUserDetails && (
        <EditUserDetails
          data={data}
          showDialog={showEditUserDetails}
          closeDialog={() => setShowEditUserDetails(false)}
        />
      )}

      {showDeactivateUser && (
        <DeactivateUserDialog
          showDialog={showDeactivateUser}
          closeDialog={() => setShowDeactivateUser(false)}
          id={data?.id}
        />
      )}

      {showReActivateUser && (
        <ReActivateUserDialog
          showDialog={showReActivateUser}
          closeDialog={() => setShowReActivateUser(false)}
          id={data?.id}
        />
      )}

      {sendingPasswordReset && <Loading />}
    </>
  );
}
